import React, { useState } from 'react';
import { Group } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Globe, Lock, MessageCircle, Users, Crown, UserPlus2, LogOut, Activity, Star, TrendingUp, Tag } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GroupMembers } from "./GroupMembers";
import { GroupPosts } from "./GroupPosts";
import { GroupSettings } from "./GroupSettings";
import { GroupNotifications } from "./GroupNotifications";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

interface GroupCardProps {
  group: Group;
  onJoin: (groupId: string) => Promise<void>;
  isSelected: boolean;
  onSelect: (groupId: string | null) => void;
  userRole: string;
  actionButton?: React.ReactNode;
}

export const GroupCard = ({ 
  group, 
  onJoin,
  isSelected, 
  onSelect,
  userRole,
  actionButton
}: GroupCardProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [isJoining, setIsJoining] = useState(false);

  const { data: isMember, isError, refetch: refetchMembership } = useQuery({
    queryKey: ['groupMembership', group.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      try {
        const { data, error } = await supabase
          .from('group_members')
          .select('*')
          .eq('group_id', group.id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching membership:', error);
          return false;
        }

        return !!data;
      } catch (error) {
        console.error('Error in membership query:', error);
        return false;
      }
    },
    retry: 1,
    enabled: !!group.id
  });

  const leaveGroupMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', group.id)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Update the group's member count
      const { data: groupData } = await supabase
        .from('groups')
        .select('members_count')
        .eq('id', group.id)
        .single();
        
      const currentCount = groupData?.members_count || 0;
      
      if (currentCount > 0) {
        const { error: updateError } = await supabase
          .from('groups')
          .update({ members_count: currentCount - 1 })
          .eq('id', group.id);
          
        if (updateError) {
          console.error('Failed to update members count:', updateError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupMembership', group.id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-members', group.id] });
      toast({
        title: "Left group",
        description: "You have successfully left the group."
      });
      setIsJoining(false);
      refetchMembership();
    },
    onError: (error) => {
      console.error('Error leaving group:', error);
      toast({
        title: "Error",
        description: "Failed to leave the group. Please try again.",
        variant: "destructive"
      });
      setIsJoining(false);
    }
  });

  const joinGroupMutation = useMutation({
    mutationFn: async () => {
      console.log("Starting join group mutation for group:", group.id);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      console.log("User authenticated:", user.id);
      
      // First check if the user is already a member
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', group.id)
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (existingMember) {
        console.log("User is already a member, skipping insert");
        return; // Skip if already a member
      }

      // Insert the group member
      console.log("Inserting new group member");
      const { error: insertError } = await supabase
        .from('group_members')
        .insert({ 
          group_id: group.id, 
          user_id: user.id 
        });
      
      if (insertError) {
        console.error("Error inserting group member:", insertError);
        throw insertError;
      }
      
      console.log("Successfully inserted group member, now updating count");
      
      // Get current member count
      const { data: groupData, error: countError } = await supabase
        .from('groups')
        .select('members_count')
        .eq('id', group.id)
        .single();
        
      if (countError) {
        console.error('Failed to get members count:', countError);
        throw countError;
      }
        
      const currentCount = groupData?.members_count || 0;
      
      // Update the group's member count with proper WHERE clause
      console.log("Updating group member count from", currentCount, "to", currentCount + 1);
      const { error: updateError } = await supabase
        .from('groups')
        .update({ members_count: currentCount + 1 })
        .eq('id', group.id);
        
      if (updateError) {
        console.error('Failed to update members count:', updateError);
        throw updateError;
      }
      
      console.log("Join group mutation completed successfully");
    },
    onSuccess: () => {
      console.log("Join group mutation success, invalidating queries");
      queryClient.invalidateQueries({ queryKey: ['groupMembership', group.id] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-members', group.id] });
      toast({
        title: "Joined group! 🎉",
        description: "Welcome to the community! Start engaging with other members."
      });
      setIsJoining(false);
      refetchMembership();
    },
    onError: (error) => {
      console.error('Error joining group:', error);
      toast({
        title: "Error",
        description: "Failed to join the group. Please try again.",
        variant: "destructive"
      });
      setIsJoining(false);
    }
  });

  const isAdmin = userRole === 'admin';
  const isCreator = group.created_by === userRole;

  const getActivityLevel = () => {
    const activityScore = (group.members_count || 0) + (group.active_discussions || 0) * 2;
    if (activityScore > 100) return { label: "Very Active", color: "text-emerald-500 bg-emerald-50" };
    if (activityScore > 50) return { label: "Active", color: "text-blue-500 bg-blue-50" };
    if (activityScore > 20) return { label: "Moderate", color: "text-yellow-500 bg-yellow-50" };
    return { label: "Quiet", color: "text-gray-500 bg-gray-50" };
  };

  const activityLevel = getActivityLevel();

  const handleGroupAction = async () => {
    try {
      if (isJoining) return;
      setIsJoining(true);
      console.log("Group action initiated, isMember:", isMember);

      if (isMember) {
        console.log("Attempting to leave group", group.id);
        await leaveGroupMutation.mutateAsync();
      } else {
        console.log("Attempting to join group", group.id);
        await joinGroupMutation.mutateAsync();
      }
    } catch (error) {
      console.error('Error with group action:', error);
      setIsJoining(false);
    }
  };

  const cardColors = [
    'card-pink', 'card-purple', 'card-blue', 'card-cyan', 'card-orange'
  ];
  const cardColor = cardColors[parseInt(group.id.slice(-1), 16) % cardColors.length];

  return (
    <div className={`
      ${cardColor} transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
      ${isSelected ? 'ring-4 ring-purple-300 dark:ring-purple-600 shadow-2xl' : ''}
      p-6 space-y-4
    `}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
              {group.name}
            </h3>
            {group.trending_score > 50 && (
              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-400 text-orange-900 border-0">
                <Star className="w-3 h-3 mr-1" />
                Hot
              </Badge>
            )}
            {group.is_private ? (
              <div className="flex items-center text-amber-600">
                <Lock className="w-4 h-4" />
              </div>
            ) : (
              <div className="flex items-center text-green-600">
                <Globe className="w-4 h-4" />
              </div>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-2">
            {group.description}
          </p>
        </div>
        {actionButton}
      </div>

      {/* Topics */}
      {group.topics && group.topics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {group.topics.slice(0, 4).map((topic, i) => (
            <Badge 
              key={i}
              variant="secondary"
              className="bg-white/60 text-gray-700 border-0 hover:bg-white/80 transition-colors"
            >
              {topic}
            </Badge>
          ))}
          {group.topics.length > 4 && (
            <Badge variant="outline" className="text-gray-500">
              +{group.topics.length - 4} more
            </Badge>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="bg-white/40 dark:bg-gray-800/40 rounded-2xl p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-700 dark:text-gray-300 mb-1">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center">
                <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{group.members_count || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Members</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-700 dark:text-gray-300 mb-1">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{group.active_discussions || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Discussions</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-700 dark:text-gray-300 mb-1">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{activityLevel.label}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Activity</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          className={`flex-1 rounded-full font-semibold transition-all duration-200 ${
            isCreator 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg' 
              : isMember 
                ? 'bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white'
                : 'btn-primary shadow-lg'
          }`}
          onClick={handleGroupAction}
          disabled={isJoining}
        >
          {isJoining ? (
            <span>Processing...</span>
          ) : isCreator ? (
            <>
              <Crown className="w-4 h-4 mr-2" />
              Your Group
            </>
          ) : isMember ? (
            <>
              <LogOut className="w-4 h-4 mr-2" />
              Leave
            </>
          ) : (
            <>
              <UserPlus2 className="w-4 h-4 mr-2" />
              Join Community
            </>
          )}
        </Button>
        
        <Button
          variant="outline"
          onClick={() => navigate(`/groups/${group.id}`)}
          className="rounded-full w-12 h-12 p-0 border-2"
        >
          <TrendingUp className="w-5 h-5" />
        </Button>
      </div>

        {isSelected && (
          <div className="mt-4">
            <Tabs defaultValue="posts" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="posts" className="text-xs md:text-sm">Posts</TabsTrigger>
                <TabsTrigger value="members" className="text-xs md:text-sm">Members</TabsTrigger>
                <TabsTrigger value="settings" className="text-xs md:text-sm">Settings</TabsTrigger>
                <TabsTrigger value="notifications" className="text-xs md:text-sm">Updates</TabsTrigger>
              </TabsList>
              <TabsContent value="posts">
                <GroupPosts groupId={group.id} isAdmin={isAdmin || isCreator} />
              </TabsContent>
              <TabsContent value="members">
                <GroupMembers groupId={group.id} isAdmin={isAdmin || isCreator} />
              </TabsContent>
              <TabsContent value="settings">
                <GroupSettings groupId={group.id} isAdmin={isAdmin || isCreator} />
              </TabsContent>
              <TabsContent value="notifications">
                <GroupNotifications groupId={group.id} isAdmin={isAdmin || isCreator} />
              </TabsContent>
            </Tabs>
          </div>
        )}
    </div>
  );
};
